import React, { useContext } from 'react';
import {
  PriceColumn,
  GoToStoreColumn,
  ProductNameColumn,
  StoreLogoColumn,
  PriceColumnRow,
} from './styles';
import {
  OfferCard,
  Content,
  ActionArea,
  CampaignBadge,
  ProductName,
  StockStatusColumn,
} from '../styles';
import Clickable from '@prisjakt/ui-clickable';
import PriceRowStoreInfoColumn from '../shared/store-info-column';
import { IOfferRowProps, ITableLinkProps } from '../types';
import { PriceLabel, ShippingLabel } from '../../components/price-column';
import { logRevenueEvent, setAntiBotFlag } from '../../utils';
import { useDispatch } from 'react-redux';
import { productPriceClick } from 'src/areas/product/actions/product';
import RatingHelper from '@prisjakt/ui-rating/src/rating-helper';
import { HighlightedOfferType } from 'src/areas/product/components/product-highlighted-offer/types';
import GoToStoreButton from '../../components/go-to-store-button';
import { AppContext } from '@prisjakt/ui-app-context';
import { getValidPriceType } from '../../../utils';
import { AdColumn } from '../shared/ad-column';

const PromotedOfferRow: React.FC<IOfferRowProps> = props => {
  const { campaign, priceDisplay, productId, type } = props;
  const {
    id,
    price,
    name,
    store,
    stock,
    deliveryOptions,
    brandRecommendedRetailer,
    externalUri,
  } = props.offer;
  const dispatch = useDispatch();
  const { t } = useContext(AppContext);
  if (!store || !id) {
    return null;
  }

  const handleClickOut = () => {
    logRevenueEvent(id, productId);
    const dispatchParams = productPriceClick(
      productId,
      props.offer.price,
      featured,
      undefined,
      false,
      false,
      type
    );
    dispatch(dispatchParams);
  };

  const rating = store?.userReviewSummary?.rating
    ? RatingHelper.convert(store.userReviewSummary.rating, 1)
    : undefined;

  const featured = Boolean(store?.featured);
  const isPromoted = type === HighlightedOfferType.Promoted;
  const tableLinkProps: ITableLinkProps = {
    rel: 'nofollow noopener',
    href: externalUri || undefined,
    onClick: handleClickOut,
    onMouseDown: setAntiBotFlag,
    onTouchStart: setAntiBotFlag,
    onKeyDown: setAntiBotFlag,
    target: '_blank',
    disabled: !externalUri,
    featured,
  };
  const priceType = getValidPriceType(props.offer.type);
  return (
    <OfferCard isPromoted={isPromoted}>
      <ActionArea
        container={Clickable}
        containerProps={{
          to: tableLinkProps.href,
          'aria-label': name,
          rel: tableLinkProps.rel,
          isExternal: true,
          onMouseDown: tableLinkProps.onMouseDown,
          onTouchStart: tableLinkProps.onTouchStart,
          onKeyDown: tableLinkProps.onKeyDown,
          target: tableLinkProps.target,
          disabled: tableLinkProps.disabled,
          onClick: tableLinkProps.onClick,
        }}
      >
        <Content>
          <AdColumn>{t('product', 'price.promoted')}</AdColumn>
          <StoreLogoColumn>
            <PriceRowStoreInfoColumn
              store={store}
              expanded={false}
              rating={rating}
              {...tableLinkProps}
            />
          </StoreLogoColumn>
          <ProductNameColumn>
            <ProductName>{name}</ProductName>
          </ProductNameColumn>
          <PriceColumn>
            {price && (
              <PriceColumnRow>
                <ShippingLabel price={price} priceDisplay={priceDisplay} />
                <div>
                  {campaign && <CampaignBadge colors={campaign.media.colors} />}
                  <PriceLabel
                    price={price}
                    storeIsFeatured={featured}
                    isCampaign={Boolean(campaign)}
                    priceDisplay={priceDisplay}
                  />
                </div>
              </PriceColumnRow>
            )}
            {stock && (
              <PriceColumnRow>
                <StockStatusColumn
                  status={stock && stock.status}
                  statusText={stock && stock.statusText}
                  deliveryOptions={deliveryOptions}
                  countryCode={store && store.countryCode}
                  toneDown={!featured}
                  isInternationalPrice={store.market === 'secondary'}
                  priceType={priceType}
                  brandRecommendedRetailer={brandRecommendedRetailer}
                />
              </PriceColumnRow>
            )}
          </PriceColumn>
          <GoToStoreColumn isPromoted={isPromoted}>
            <GoToStoreButton visible disabled={!featured} featured={featured} />
          </GoToStoreColumn>
        </Content>
      </ActionArea>
    </OfferCard>
  );
};

export default PromotedOfferRow;
